import { FileUploader, Label } from '@portal/components'
import { csv2jsonAsync } from 'json-2-csv'
import React, { useCallback, useState } from 'react'

import { validateJsonObjectContent } from '../../utils'
import { validateCsvStructure } from './utils'

export type ImportByFileProps = {
  /**
   * The file that has been uploaded
   */
  uploadedFile?: File
  /**
   * Callback to be called when a file is dropped
   */
  onDroppedFile(file?: File, contents?: string, format?: 'csv' | 'json'): void
  /**
   * Whether the file uploader is disabled
   */
  disabled?: boolean
  /**
   * Optional className to apply to the file uploader
   */
  className?: string
}

/**
 * Detects the format of a file based on its content and name. If no extension is provided,
 * it will try to detect the format based on the content.
 * @param content The content of the file
 * @param fileName The name of the file (optional)
 * @returns 'csv' if the file is a CSV, 'json' if the file is a JSON, undefined otherwise
 */
const detectFileFormat = (
  content: string,
  fileName?: string,
): 'csv' | 'json' | undefined => {
  const trimmed = content.trim()

  if (fileName) {
    const extension = fileName.split('.').pop()?.toLowerCase()

    return extension === 'json' && validateJsonObjectContent(trimmed)
      ? 'json'
      : extension === 'csv' && validateCsvStructure(trimmed)
        ? 'csv'
        : undefined
  }

  return validateJsonObjectContent(trimmed)
    ? 'json'
    : validateCsvStructure(trimmed)
      ? 'csv'
      : undefined
}

export const DataImportFile = ({
  uploadedFile,
  onDroppedFile,
  disabled,
  className,
}: ImportByFileProps) => {
  const [detectedFormat, setDetectedFormat] = useState<
    'csv' | 'json' | undefined
  >(undefined)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const onFileDrop = useCallback(
    async (acceptedFiles: File[]) => {
      setErrorMessage(null)

      try {
        const droppedFile = acceptedFiles[0]

        if (droppedFile) {
          const reader = new FileReader()

          reader.addEventListener('load', async (event) => {
            const textValue = (event.target?.result as string | null) || ''
            const format = detectFileFormat(textValue, droppedFile.name)

            if (!format) {
              setErrorMessage('Failed to detect file format')
              return
            }

            const formatted =
              format === 'csv'
                ? await csv2jsonAsync(textValue)
                : JSON.parse(textValue)

            if (format && formatted) {
              setDetectedFormat(format)
              onDroppedFile(droppedFile, formatted)
            }
          })

          reader.readAsText(droppedFile)
        }
      } catch {
        setDetectedFormat(undefined)
      }
    },
    [onDroppedFile],
  )

  /**
   * Renders the prompt to be displayed when a file is uploaded
   */
  const renderUploadPrompt = () => {
    if (uploadedFile && !errorMessage) {
      return (
        <p className="mb-2 text-sm text-gray-500 dark:text-gray-300">
          <span className="font-semibold">{uploadedFile.name}</span> loaded.
          <span> (Detected format: {detectedFormat?.toUpperCase()})</span>
        </p>
      )
    } else if (errorMessage) {
      return (
        <p className="mb-2 text-sm text-red-500 dark:text-red-400">
          <span className="font-semibold">{errorMessage}</span>
        </p>
      )
    }

    return undefined
  }

  return (
    <div
      className="flex w-full flex-col gap-2"
      data-testid="dataImportFileSection"
    >
      <Label>Import with a JSON or CSV file</Label>
      <FileUploader
        onDrop={onFileDrop}
        prompt={renderUploadPrompt()}
        icon={uploadedFile ? 'fileAdded' : 'cloud'}
        disabled={disabled}
        className={className}
      />
    </div>
  )
}
